import React, { useState, useEffect } from 'react';
import { Users, Plus, X, Pencil, Trash2, ChevronDown, ChevronUp, Package, AlertCircle, BarChart3 } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerPricing, setCustomerPricing] = useState({});
  const [error, setError] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    address: '',
    phone: '',
    email: '',
    taxId: '',
    tvshExempt: false,
    notes: ''
  });
  
  // Pricing form
  const [showPricingForm, setShowPricingForm] = useState(null);
  const [pricingData, setPricingData] = useState({ productId: '', pricePerUnit: '' });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch customers and products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [customersRes, productsRes] = await Promise.all([
          fetch(`${API_URL}/api/customers`),
          fetch(`${API_URL}/api/presets`)
        ]);
        
        if (customersRes.ok) {
          const customersData = await customersRes.json();
          setCustomers(customersData);
        }
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_URL]);

  // Fetch pricing when customer is expanded
  const fetchCustomerPricing = async (customerId) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/pricing`);
      if (response.ok) {
        const data = await response.json();
        setCustomerPricing(prev => ({ ...prev, [customerId]: data }));
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  // Toggle customer expansion
  const toggleCustomer = (customerId) => {
    if (expandedCustomer === customerId) {
      setExpandedCustomer(null);
    } else {
      setExpandedCustomer(customerId);
      if (!customerPricing[customerId]) {
        fetchCustomerPricing(customerId);
      }
    }
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    const payload = {
      name: formData.name,
      company: formData.company || null,
      address: formData.address || null,
      phone: formData.phone || null,
      email: formData.email || null,
      taxId: formData.taxId || null,
      tvshExempt: !!formData.tvshExempt,
      notes: formData.notes || null
    };

    try {
      if (editingCustomer) {
        const response = await fetch(`${API_URL}/api/customers/${editingCustomer.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const updated = await response.json();
          setCustomers(prev => prev.map(c => c.id === editingCustomer.id ? updated : c));
        } else {
          setError('Failed to update customer');
          return;
        }
      } else {
        const response = await fetch(`${API_URL}/api/customers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (response.ok) {
          const newCustomer = await response.json();
          setCustomers(prev => [...prev, newCustomer]);
        } else {
          setError('Failed to create customer');
          return;
        }
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving customer:', error);
      setError('Network error. Please try again.');
    }
  };

  // Delete customer
  const handleDelete = async (customerId, customerName) => {
    if (!window.confirm(`Delete "${customerName}"? This will also remove all their pricing.`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setCustomers(prev => prev.filter(c => c.id !== customerId));
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({ name: '', company: '', address: '', phone: '', email: '', taxId: '', tvshExempt: false, notes: '' });
    setShowAddForm(false);
    setEditingCustomer(null);
  };

  // Edit customer
  const startEdit = (customer) => {
    setFormData({
      name: customer.name,
      company: customer.company || '',
      address: customer.address || '',
      phone: customer.phone || '',
      email: customer.email || '',
      taxId: customer.taxId || '',
      tvshExempt: !!customer.tvshExempt,
      notes: customer.notes || ''
    });
    setEditingCustomer(customer);
    setShowAddForm(true);
  };

  // Add/update pricing
  const handlePricingSubmit = async (customerId) => {
    if (!pricingData.productId || !pricingData.pricePerUnit) return;
    
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId,
          productId: pricingData.productId,
          pricePerUnit: parseFloat(pricingData.pricePerUnit)
        })
      });
      
      if (response.ok) {
        await fetchCustomerPricing(customerId);
        setPricingData({ productId: '', pricePerUnit: '' });
        setShowPricingForm(null);
        setError(null);
      } else {
        setError('Failed to save pricing');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      setError('Network error saving pricing');
    }
  };

  // Delete pricing
  const handleDeletePricing = async (customerId, productId) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/pricing/${productId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        await fetchCustomerPricing(customerId);
        setError(null);
      } else {
        setError('Failed to delete pricing');
      }
    } catch (error) {
      console.error('Error deleting pricing:', error);
      setError('Network error deleting pricing');
    }
  };

  // Get product name by ID
  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.label || 'Unknown';
  };

  // Get products not yet priced for a customer
  const getUnpricedProducts = (customerId) => {
    const pricedIds = (customerPricing[customerId] || []).map(p => p.productId);
    return products.filter(p => !pricedIds.includes(p.id));
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-purple-500" />
          <h1 className="text-xl sm:text-2xl font-bold text-white">Customers</h1>
          <span className="text-sm text-zinc-500">({customers.length})</span>
        </div>
        <Button
          onClick={() => setShowAddForm(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Customer</span>
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-900/30 border border-red-800 rounded-lg text-red-400" data-testid="error-message">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              {editingCustomer ? 'Edit Customer' : 'Add Customer'}
            </h2>
            <button onClick={resetForm} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm">Name *</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Company</Label>
                <Input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-zinc-400 text-sm">Address</Label>
              <Input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-200"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-sm">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-sm">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-sm">NUI (Numri Unik Identifikues)</Label>
                <Input
                  type="text"
                  value={formData.taxId}
                  onChange={(e) => setFormData(prev => ({ ...prev, taxId: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700 text-zinc-200"
                  data-testid="customer-taxid-input"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-md bg-zinc-800/50 border border-zinc-700 hover:border-zinc-600 transition-colors w-full">
                  <input
                    type="checkbox"
                    checked={!!formData.tvshExempt}
                    onChange={(e) => setFormData(prev => ({ ...prev, tvshExempt: e.target.checked }))}
                    className="accent-amber-500"
                    data-testid="customer-tvsh-exempt-toggle"
                  />
                  <span className="text-sm text-zinc-300">TVSH-exempt (e.g. export)</span>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 text-sm">Notes</Label>
              <Input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-zinc-200"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={resetForm} className="border-zinc-700 text-zinc-400" data-testid="customer-cancel-btn">
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="customer-submit-btn">
                {editingCustomer ? 'Update' : 'Add'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Customers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">No customers yet</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="customers-list">
          {customers.map(customer => {
            const isExpanded = expandedCustomer === customer.id;
            const pricing = customerPricing[customer.id] || [];
            
            return (
              <div key={customer.id} className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden" data-testid={`customer-card-${customer.id}`}>
                {/* Customer Header */}
                <div className="flex items-center justify-between p-4">
                  <button
                    onClick={() => toggleCustomer(customer.id)}
                    className="flex-1 flex items-center gap-4 text-left"
                    data-testid={`customer-expand-${customer.id}`}
                  >
                    <div>
                      <div className="text-lg font-semibold text-white flex items-center gap-2">
                        {customer.name}
                        {customer.tvshExempt && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid={`customer-tvsh-exempt-badge-${customer.id}`} title="TVSH-exempt (e.g. export)">
                            TVSH-EXEMPT
                          </span>
                        )}
                      </div>
                      {customer.company && (
                        <div className="text-sm text-zinc-500">{customer.company}</div>
                      )}
                      {customer.taxId && (
                        <div className="text-xs text-zinc-600">NUI {customer.taxId}</div>
                      )}
                    </div>
                  </button>
                  
                  <div className="flex items-center gap-4">
                    {pricing.length > 0 && (
                      <span className="text-sm text-purple-400">
                        {pricing.length} product{pricing.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); window.location.href = `/analytics?customer=${customer.id}`; }}
                        className="h-8 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-zinc-700"
                        title="View this customer's analytics — AR, payment behavior, purchase history"
                        data-testid={`customer-analytics-${customer.id}`}
                      >
                        <BarChart3 className="w-3.5 h-3.5 mr-1" />
                        Analytics
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => startEdit(customer)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-white hover:bg-zinc-700"
                        data-testid={`customer-edit-${customer.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(customer.id, customer.name)}
                        className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400 hover:bg-zinc-700"
                        data-testid={`customer-delete-${customer.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <button onClick={() => toggleCustomer(customer.id)} className="p-2 text-zinc-400">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-4" data-testid={`customer-details-${customer.id}`}>
                    {/* Contact Info */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 text-sm">
                      {customer.phone && (
                        <div>
                          <div className="text-zinc-500">Phone</div>
                          <div className="text-zinc-300">{customer.phone}</div>
                        </div>
                      )}
                      {customer.email && (
                        <div>
                          <div className="text-zinc-500">Email</div>
                          <div className="text-zinc-300">{customer.email}</div>
                        </div>
                      )}
                      {customer.address && (
                        <div className="col-span-2">
                          <div className="text-zinc-500">Address</div>
                          <div className="text-zinc-300">{customer.address}</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Products & Pricing */}
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-zinc-400">Products & Pricing</h4>
                        <Button
                          size="sm"
                          onClick={() => setShowPricingForm(showPricingForm === customer.id ? null : customer.id)}
                          className="h-7 text-xs bg-purple-600 hover:bg-purple-700"
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add Product
                        </Button>
                      </div>
                      
                      {/* Add Pricing Form */}
                      {showPricingForm === customer.id && (
                        <div className="flex gap-2 mb-3 p-3 bg-zinc-800/50 rounded-lg" data-testid="pricing-form">
                          <select
                            value={pricingData.productId}
                            onChange={(e) => setPricingData(prev => ({ ...prev, productId: e.target.value }))}
                            className="flex-1 h-9 px-3 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-200 text-sm"
                            data-testid="pricing-product-select"
                          >
                            <option value="">Select product...</option>
                            {getUnpricedProducts(customer.id).map(product => (
                              <option key={product.id} value={product.id}>{product.label}</option>
                            ))}
                          </select>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Price"
                            value={pricingData.pricePerUnit}
                            onChange={(e) => setPricingData(prev => ({ ...prev, pricePerUnit: e.target.value }))}
                            className="w-24 h-9 bg-zinc-800 border-zinc-700 text-zinc-200 text-sm"
                            data-testid="pricing-price-input"
                          />
                          <Button
                            size="sm"
                            onClick={() => handlePricingSubmit(customer.id)}
                            className="h-9 bg-purple-600 hover:bg-purple-700"
                            data-testid="pricing-submit-btn"
                          >
                            Add
                          </Button>
                        </div>
                      )}
                      
                      {/* Pricing List */}
                      {pricing.length === 0 ? (
                        <p className="text-sm text-zinc-600">No products assigned</p>
                      ) : (
                        <div className="space-y-1" data-testid="pricing-list">
                          {pricing.map(p => (
                            <div
                              key={p.id}
                              className="flex items-center justify-between p-2 bg-zinc-800/30 rounded"
                              data-testid={`pricing-item-${p.productId}`}
                            >
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-zinc-500" />
                                <span className="text-sm text-zinc-300">{getProductName(p.productId)}</span>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-purple-400">
                                  ${p.pricePerUnit.toLocaleString()}/unit
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePricing(customer.id, p.productId)}
                                  className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400"
                                  data-testid={`pricing-delete-${p.productId}`}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
